import { type FC, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { AccountIntakeModal } from '../intake/AccountIntakeModal'
import { useAccountTypeCatalog } from '../hooks/useAccountTypeCatalog'
import { useResolvedConnector } from '../hooks/useResolvedConnector'
import { contactOptionOf } from '../domain/contactFallback'
import { createEmptyAccount, type WizardAccount } from '../domain/types'
import { type User } from '../../../models/User'
import { useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { accountOpeningSource, startedEvent } from '../domain/tracking'

interface ContactAccountIntakeProps {
  contact: User
  onCancel: () => void
}

export const ContactAccountIntake: FC<ContactAccountIntakeProps> = ({ contact, onCancel }) => {
  const contactId = contact.id
  const contactName = contact.name
  const contactOptions = useMemo(() => [contactOptionOf(contact)], [contact])
  const accountId = useAppSelector(selectAccountId)
  const navigate = useNavigate()

  const { connectors, connector, setConnector } = useResolvedConnector()
  const catalog = useAccountTypeCatalog(accountId, connector ?? '')
  const { trackingClient } = useTracking()

  const handleContinue = (intakeAccounts: WizardAccount[]) => {
    trackingClient.track(startedEvent(accountOpeningSource(contactId), intakeAccounts, catalog.accountTypes, catalog.selectors))
    navigate(`/workspace/account-opening?${new URLSearchParams({ contactId })}`, {
      state: { intakeAccounts, intakeConnector: connector },
    })
  }

  return (
    <AccountIntakeModal
      show
      connectors={connectors}
      connector={connector}
      onConnectorChange={setConnector}
      catalog={catalog}
      initialAccounts={[createEmptyAccount()]}
      contactOptions={contactOptions}
      defaultHolderName={contactName}
      defaultHolderContactId={contactId}
      onCancel={onCancel}
      onContinue={handleContinue}
    />
  )
}
